<script>
    import { onMount } from "svelte";
    import { theme } from "$stores/themeStore";
    import { user, isAuthenticated } from "$stores/sessionStore";
    import { navigate as goto } from 'svelte-routing';
    import ParticipantDetails from "$lib/ParticipantDetails.svelte";
    import {
      selectedStudy,
      selectedSite,
      selectedParticipant,
    } from "$stores/selectionStore";
    import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
    import {
      faChevronLeft,
      faChevronRight,
      faSquarePlus,
      faTrashCan,
      faPencil,
      faRotate,
    } from "@fortawesome/pro-light-svg-icons";
    
    // Use local API endpoints instead of direct API with key
    const API_BASE_URL = '/api/proxy';
  
    import { page } from '$stores/pageStore';
    import { get } from "svelte/store";
  
    let currentTheme = "light";
    let currentUser = null;
    let currentUserAuthenticated = null;
    let duplicates = [];
    let resultCount = 0;
    let currentPage = 1;
    let totalPages = 1;
    let limit = 100;
    let isLoading = true;
    let searchText = "";
    let selectedEvent = "ALL";
    let selectedLogLevel = "ALL";
  
    let study, site, participant;
  
    onMount(async () => {
      theme.subscribe((value) => {
        currentTheme = value;
      });
  
      user.subscribe((value) => {
        currentUser = value;
      });
  
      selectedStudy.subscribe((value) => {
        //console.log("selectedStudy updated:", value);
        study = value;
      });
  
      selectedSite.subscribe((value) => {
        //console.log("selectedSite updated:", value);
        site = value;
      });
  
      selectedParticipant.subscribe((value) => {
        //console.log("selectedParticipant updated:", value);
        participant = value;
      });
  
      if (participant) {
        fetchDuplicates(1);
      }
    });
  
    async function fetchDuplicates(page = 1) {
      try {
        let url = `${API_BASE_URL}/glucoseReadings/duplicates?page=${page}&pageSize=${limit}&subjectGUID=${participant._id}`;
  
        const res = await fetch(url);
  
        if (!res.ok) {
          throw new Error("Failed to fetch compliance");
        }
  
        const responseData = await res.json();
  
        resultCount = responseData.totalCount || 0;
        currentPage = responseData.page || 1;
        totalPages = responseData.totalPages || 1;
  
        duplicates = responseData.data;

        //console.log("duplicates:", duplicates);
      } catch (err) {
        console.error("Error fetching log entries:", err);
        duplicates = [];
      }
    }
  
    function handleRefresh() {
      //console.log("handleRefresh");
      fetchDuplicates(1);
    }
  
    function handlePageChange(newPage) {
      if (newPage > 0 && newPage <= totalPages) {
        fetchDuplicates(newPage);
      }
    }
  </script>
  
  <div class="flex justify-between ph-2 pb-0">
    <h2 class="text-s mb-4 text-sky-800 dark:text-white">
      {study ? "Protocol: " + study.protocolNumber + " - " : ""}{site
        ? "Site: " + site.siteName
        : ""}{participant
        ? " - Subject: " + participant.subjectId
        : " - Duplicates"}
    </h2>
  </div>
  
  {#if participant}
    <ParticipantDetails showOptionsMenu={false} />
  {/if}
  
  <!-- Title and Filters -->
  <div class="flex justify-between pt-4 ph-2 pb-0">
    <h2 class="text-l mb-4 mx-4 text-sky-800 dark:text-white">Duplicate Glucose Readings</h2>
    <div class="flex items-center gap-4 mb-4">
      <div class="text-black dark:text-white">
        <button on:click={handleRefresh} class="pagination mx-1 p-0">
          <FontAwesomeIcon
            icon={faRotate}
            class="text-sky-800 dark:text-white"
          />
        </button>
      </div>
    </div>
  </div>
  
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr
          class="text-sm text-white dark:text-slate-300 bg-stone-400/70 dark:bg-gray-725/70"
        >
          <th class="px-4 py-2 text-left bold">Id</th>
          <th class="px-4 py-2 text-left bold">Subject Activity GUID</th>
          <th class="px-4 py-2 text-left bold">Sensor</th>
          <th class="px-4 py-2 text-left bold">Reading Id</th>
          <th class="px-4 py-2 text-left bold">Read / Received</th>
          <th class="px-4 py-2 text-left bold">Value</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Count</th>
        </tr>
      </thead>
      <tbody>
        {#each duplicates as duplicate, index}
          <tr
            class="text-sm hover:bg-stone-300 dark:hover:bg-gray-900/30 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-white dark:bg-gray-700'
              : 'bg-stone-100 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2 cursor-pointer">{duplicate._id}</td>
            <td class="px-4 py-2 cursor-pointer">{duplicate.subjectActivityGUID}</td>
            <td class="px-4 py-2 cursor-pointer">{duplicate.externalId}</td>
            <td class="px-4 py-2 cursor-pointer">{duplicate.externalReadingId}</td>
            <td class="px-4 py-2 cursor-pointer w-48">
                {#if duplicate.read}
                    {new Date(duplicate.read).toLocaleDateString()}
                    {new Date(duplicate.read).toLocaleTimeString()}
                {/if}
                <br>
                {#if duplicate.received}
                    {new Date(duplicate.received).toLocaleDateString()}
                    {new Date(duplicate.received).toLocaleTimeString()}
                {/if}
            </td>
            <td class="px-4 py-2">{duplicate.value}{duplicate.raw && duplicate.raw.includes('"isBackfilled":true') ? ' (b)' : ''}{duplicate.raw && duplicate.raw.includes('"wasBackfilled":true') ? ' (b)' : ''} {duplicate.unit}</td>

            <td class="px-4 py-2 cursor-pointer">{duplicate.type ?? "n/a"}</td>
            <td class="px-4 py-2 cursor-pointer">{duplicate.duplicateCount}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
  
  <!-- Add Control Buttons -->
  <div class="flex justify-between p-2 py-3">
    <div class="pagination">
      <button
        on:click={() => handlePageChange(currentPage - 1)}
        class="pagination mx-1 p-0"
        disabled={currentPage === 1}
      >
        <FontAwesomeIcon
          icon={faChevronLeft}
          class="text-sky-800 dark:text-white"
        />
      </button>
      <span class="mx-1 p-0 text-black dark:text-white"
        >Page {currentPage} of {totalPages}</span
      >
      <button
        on:click={() => handlePageChange(currentPage + 1)}
        class="pagination mx-1 p-0"
        disabled={currentPage === totalPages}
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          class="text-sky-800 dark:text-white"
        />
      </button>
    </div>
  </div>
  
  <style>
    .pagination button[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }
  </style>
  